import React from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Play, Pause, BookOpen, Download, Share2, Check, Loader2 } from 'lucide-react';

const NewsCard = ({ article, onPlaySummary, onPlayFull, onTextRead, onDownload, onShare, isPlayingSummary, isPlayingFull, isDownloaded, isDownloading, darkMode = false }) => {
  // Format date from published_at (already in IST) to "2nd Jan'26" format
  // Parse as UTC to prevent timezone conversion
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Parse the ISO string and extract components directly to avoid timezone issues
      const dateParts = dateString.split('T')[0].split('-');
      const year = dateParts[0];
      const month = parseInt(dateParts[1]) - 1;
      const day = parseInt(dateParts[2]);
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[month];
      const yearShort = year.slice(-2);
      
      // Add ordinal suffix (st, nd, rd, th)
      const getOrdinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };
      
      return `${getOrdinal(day)} ${monthName}'${yearShort}`;
    } catch (error) {
      return '';
    }
  };

  return (
    <Card className={`overflow-hidden mb-4 shadow-sm hover:shadow-md transition-shadow ${darkMode ? 'bg-gray-800 border-gray-700' : ''}`}>
      {/* Article Image */}
      <div className="relative">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-48 object-cover"
        />
        {/* Category Badge and Share Icon */}
        <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
          <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
            {article.category}
          </span>
          <button
            onClick={() => onShare(article)}
            className="bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors"
          >
            <Share2 className="w-4 h-4 text-gray-700" />
          </button>
        </div>
      </div>

      {/* Article Content */}
      <div className="p-4">
        <h3 className={`text-lg font-semibold mb-2 leading-snug ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {article.title}
        </h3>
        <p className={`text-sm mb-3 line-clamp-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {article.content}
        </p>

        {/* Source, Date and Duration */}
        <div className={`flex items-center text-xs mb-4 gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <span>{article.source}</span>
          <span>•</span>
          <span>{formatDate(article.published_at)}</span>
          <span>•</span>
          <span>{article.duration}</span>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-4 gap-2">
          <Button
            onClick={() => onPlaySummary(article)}
            className="bg-blue-700 hover:bg-blue-800 text-white flex items-center justify-center gap-1 py-5 px-1 text-[10px] sm:text-xs"
          >
            {isPlayingSummary ? (
              <Pause className="w-3 h-3 flex-shrink-0" fill="currentColor" />
            ) : (
              <Play className="w-3 h-3 flex-shrink-0" fill="currentColor" />
            )}
            <span className="truncate">Summary</span>
          </Button>
          <Button
            onClick={() => onPlayFull(article)}
            variant="outline"
            className={`flex items-center justify-center gap-1 py-5 px-1 text-[10px] sm:text-xs ${
              darkMode 
                ? 'border-blue-500 text-blue-400 hover:bg-blue-900/30' 
                : 'border-blue-700 text-blue-700 hover:bg-blue-50'
            }`}
          >
            {isPlayingFull ? (
              <Pause className="w-3 h-3 flex-shrink-0" />
            ) : (
              <Play className="w-3 h-3 flex-shrink-0" />
            )}
            <span className="truncate">Full</span>
          </Button>
          <Button
            onClick={() => onTextRead(article)}
            variant="outline"
            className={`flex items-center justify-center py-5 ${
              darkMode 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <BookOpen className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => onDownload(article)}
            variant="outline"
            disabled={isDownloading || isDownloaded}
            className={`flex items-center justify-center py-5 ${
              isDownloaded 
                ? 'border-green-500 text-green-600 bg-green-50' 
                : isDownloading
                ? 'border-blue-500 text-blue-600'
                : darkMode
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {isDownloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isDownloaded ? (
              <Check className="w-4 h-4" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default NewsCard;
